import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FiCheck, FiCopy, FiEdit2, FiExternalLink, FiTrash2 } from "react-icons/fi";
import PageLayout from "../components/layout/PageLayout";
import InlineBanner from "../components/ui/InlineBanner";
import PropertyDetailsComponent from "../components/propertyComponents/PropertyDetailsComponent";
import { getHostDisplayName } from "../components/hostComponents/HostOverviewCard";
import { deleteProperty, getPropertyDetails, updateProperty } from "../store/thunks/propertyActions";
import { updatePropertyReset } from "../store/slices/propertySlices";
import { getHostDetails } from "../store/thunks/HostActions";

const EMPTY_ADDRESS = { line1: "", line2: "", city: "", state: "", zipCode: "" };
const EMPTY_CREDENTIALS = { description: "", keyInfo: "", listingTitle: "" };

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_error) {
    // fall through
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch (_error) {
    copied = false;
  }
  document.body.removeChild(textArea);
  return copied;
};

function Pill({ label, tone = "zinc" }) {
  const tones = {
    zinc: "bg-zinc-100 text-zinc-600 ring-zinc-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tones[tone] || tones.zinc}`}>
      {label}
    </span>
  );
}

function PropertyDetailsScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hid, pid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { property, loading: loadingProperty, error: errorProperty } = useSelector((state) => state.propertyDetails);
  const { host } = useSelector((state) => state.hostDetails);
  const { loading: saving, success: successUpdate, error: errorUpdate } = useSelector((state) => state.propertyUpdate);

  const [editMode, setEditMode] = useState(searchParams.get("edit") === "true");
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [credentials, setCredentials] = useState(EMPTY_CREDENTIALS);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState(null);

  const propertyLoaded = property?._id === pid;

  // Hydrate the form from the loaded property.
  const resetForm = useCallback(() => {
    if (!propertyLoaded) return;
    setAddress({ ...EMPTY_ADDRESS, ...(property.address || {}) });
    setCredentials({
      description: property.description || "",
      keyInfo: property.keyInfo || "",
      listingTitle: property.listingTitle || "",
    });
    setCheckInTime(toDate(property.checkInTime));
    setCheckOutTime(toDate(property.checkOutTime));
  }, [property, propertyLoaded]);

  useEffect(() => {
    if (pid) dispatch(getPropertyDetails(pid));
  }, [dispatch, pid]);

  useEffect(() => {
    if (hid && host?._id !== hid) dispatch(getHostDetails(hid));
  }, [dispatch, hid, host?._id]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  useEffect(() => {
    if (!successUpdate && !errorUpdate) return undefined;
    if (successUpdate) {
      setNotice({ tone: "success", message: "Property updated." });
      setEditMode(false);
      if (searchParams.get("edit")) setSearchParams({}, { replace: true });
      dispatch(getPropertyDetails(pid));
    }
    if (errorUpdate) setNotice({ tone: "error", message: errorUpdate });
    dispatch(updatePropertyReset());
    return undefined;
  }, [dispatch, errorUpdate, pid, searchParams, setSearchParams, successUpdate]);

  useEffect(() => {
    if (!notice) return undefined;
    const timeoutId = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(timeoutId);
  }, [notice]);

  const handleSave = () => {
    const payload = { ...credentials, address };
    if (checkInTime) payload.checkInTime = checkInTime;
    if (checkOutTime) payload.checkOutTime = checkOutTime;
    dispatch(updateProperty({ propertyCredentials: payload, pid }));
  };

  const handleCancel = () => {
    resetForm();
    setEditMode(false);
    if (searchParams.get("edit")) setSearchParams({}, { replace: true });
  };

  const handleDelete = async () => {
    const label = property?.address?.line1 || "this property";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    const action = await dispatch(deleteProperty(pid));
    if (deleteProperty.fulfilled.match(action)) {
      navigate(`/hostList/${hid}`, { replace: true });
    } else {
      setNotice({ tone: "error", message: action.payload?.error || "Failed to delete property." });
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(property?.uniqueLink);
    if (!ok) {
      setNotice({ tone: "error", message: "Could not copy the guest link." });
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const title = useMemo(() => {
    if (!propertyLoaded) return "Property";
    return property.address?.line1 || property.listingTitle || "Property";
  }, [property, propertyLoaded]);

  const subtitle = useMemo(() => {
    if (!propertyLoaded) return loadingProperty ? "Loading…" : "";
    return [property.address?.city, property.address?.state, property.address?.zipCode].filter(Boolean).join(", ");
  }, [loadingProperty, property, propertyLoaded]);

  const hostName = host?._id === hid ? getHostDisplayName(host) : "Host";

  return (
    <PageLayout
      title={title}
      subtitle={subtitle}
      breadcrumbs={[
        { label: "Property Managers", path: "/hostList" },
        { label: hostName, path: `/hostList/${hid}` },
        { label: title },
      ]}
      showSearch={false}
      fullWidth
      actions={(
        <>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!property?.uniqueLink}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
              copied
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {copied ? <FiCheck className="h-4 w-4" /> : <FiCopy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy guest link"}
          </button>
          {editMode ? (
            <>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-zinc-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditMode(true)}
                disabled={!propertyLoaded}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50"
              >
                <FiEdit2 className="h-4 w-4" />
                Edit
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={!propertyLoaded}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
              >
                <FiTrash2 className="h-4 w-4" />
                Delete
              </button>
            </>
          )}
        </>
      )}
    >
      {errorProperty ? <InlineBanner tone="error">{errorProperty}</InlineBanner> : null}
      {notice ? (
        <InlineBanner tone={notice.tone} onClose={() => setNotice(null)}>{notice.message}</InlineBanner>
      ) : null}

      {loadingProperty && !propertyLoaded ? (
        <div className="animate-pulse space-y-3">
          <div className="h-16 rounded-2xl border border-zinc-200 bg-white" />
          <div className="grid grid-cols-1 gap-3 laptop:grid-cols-12">
            <div className="h-72 rounded-2xl border border-zinc-200 bg-white laptop:col-span-7" />
            <div className="h-40 rounded-2xl border border-zinc-200 bg-white laptop:col-span-5" />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <section className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-1.5">
              {editMode ? <Pill label="Editing" tone="sky" /> : <Pill label="Read only" />}
              {property?.propertySource ? <Pill label={`Source: ${property.propertySource}`} /> : null}
              {property?.uniqueId ? (
                <span className="font-mono text-xs text-zinc-500">ID {property.uniqueId}</span>
              ) : null}
            </div>
            {property?.uniqueLink ? (
              <a
                href={property.uniqueLink}
                target="_blank"
                rel="noreferrer"
                className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              >
                Open guest page
                <FiExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
          </section>

          <PropertyDetailsComponent
            address={address}
            credentials={credentials}
            checkInTime={checkInTime}
            checkOutTime={checkOutTime}
            readOnly={!editMode}
            onAddressChange={setAddress}
            onCredentialsChange={setCredentials}
            onCheckInChange={setCheckInTime}
            onCheckOutChange={setCheckOutTime}
          />
        </div>
      )}
    </PageLayout>
  );
}

export default PropertyDetailsScreen;
